import React, { useCallback, useState } from 'react'
import { Pressable, StyleSheet, ScrollView, ViewStyle } from 'react-native'
import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import {
  DNABox,
  DNAIcon,
  Iffy,
  DNAText,
  DNAButton,
  DNAButtonProps,
  DNADivider,
  DNACard,
  Rotate,
  DNAIconStyleProps,
} from '@alucio/lux-ui'
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors'
import { DNAPopover } from 'src/components/DNA'
import { WebLinking as Linking } from '@alucio/core'

import useScreenNav from 'src/components/DNA/hooks/useScreenNav'
import { useAppSettings, DeviceMode } from 'src/state/context/AppSettings'
import { useSyncState, useSyncStatus, SyncStatusORM } from 'src/state/redux/selector/cache'
import { useAllDocumentVersionMap } from 'src/state/redux/selector/document'
import { bytesToSize } from 'src/utils/documentHelpers'
import workerChannel from 'src/worker/channels/workerChannel'
import { useCRMStatus } from 'src/hooks/useCRMStatus'
import { CRMStatus } from 'src/screens/Profile/CRMIntegration'
interface Styles {
  centerIconUI2: ViewStyle,
  menuBorder: ViewStyle,
  cardContainer: ViewStyle,
}

type DeviceModeStyles = Record<DeviceMode, StyleSheet.NamedStyles<Styles>>

const deviceModeStyles: DeviceModeStyles = {
  desktop: StyleSheet.create({
    centerIconUI2: {
      position: 'absolute',
      right: -1,
      top: 14,
      backgroundColor: colors['color-white'],
    },
    menuBorder: {
      shadowColor: colors['color-black'],
      shadowOffset: {
        width: 0,
        height: 9,
      },
      shadowOpacity: 0.12,
      shadowRadius: 12.35,
      borderRadius: 4,
    },
    cardContainer: {
      width: 400,
    },
  }),
  tablet: StyleSheet.create({
    centerIconUI2: {
      position: 'absolute',
      right: 4,
      top: 22,
      backgroundColor: colors['color-white'],
    },
    centerIcon: {
      position: 'absolute',
      // @ts-expect-error - Works fine in web
      transform: [{ translateX: '40%' }, { translateY: '95%' }],
    },
    menuBorder: {
      shadowColor: colors['color-black'],
      shadowOffset: {
        width: 0,
        height: 9,
      },
      shadowOpacity: 0.12,
      shadowRadius: 12.35,
      borderRadius: 4,
    },
    cardContainer: {
      width: 400,
      padding: 24,
    },
  }),
}

const S = StyleSheet.create({
  statusInfoContainer: {
    padding: 24,
    borderWidth: 1,
    borderColor: colors['color-gray-80'],
  },
  failedScrollContainer: {
    flex: 1,
    maxHeight: 200,
  },
})

type SyncStatusDefinition = {
  statuses: string[],
  buttonIcon: string,
  labelIcon: string,
  status: DNAIconStyleProps['status'],
  label: string,
}

const syncStatusDefinition: SyncStatusDefinition[] = [
  {
    statuses: ['offline'],
    buttonIcon: '',
    labelIcon: 'cloud-slash',
    status: 'warning',
    label: "Looks like you're offline.",
  },
  {
    statuses: ['online.idle.init'],
    buttonIcon: 'arrows-clockwise',
    labelIcon: 'arrows-clockwise',
    status: 'primary',
    label: 'Waiting for sync...',
  },
  {
    statuses: ['online.sync.prepare'],
    buttonIcon: 'arrows-clockwise',
    labelIcon: 'arrows-clockwise',
    status: 'primary',
    label: 'Preparing download...',
  },
  {
    statuses: ['online.sync.thumbnails', 'online.sync.pagesJson', 'online.sync.content', 'online.sync.determine'],
    buttonIcon: 'arrows-clockwise',
    labelIcon: 'arrows-clockwise',
    status: 'primary',
    label: 'Downloading in progress...',
  },
  {
    statuses: ['online.paused'],
    buttonIcon: 'pause-circle-fill',
    labelIcon: 'pause-circle-fill',
    status: 'primary',
    label: 'Downloading paused...',
  },
  {
    statuses: ['online.idle.pending'],
    buttonIcon: 'check-circle-fill',
    labelIcon: 'check-circle-fill',
    status: 'success',
    label: "You're all synced for offline use.",
  },
  {
    statuses: ['online.idle.error'],
    buttonIcon: 'warning-circle-fill',
    labelIcon: 'warning-circle-fill',
    status: 'warning',
    label: 'Downloading complete',
  },
]

const getSyncStatusDefinition = (syncStatus: SyncStatusORM): SyncStatusDefinition | undefined => (
  syncStatusDefinition.find(definition => (
    definition
      .statuses
      .some(defStatus => syncStatus.matches(defStatus))
  )) ??
  {
    statuses: ['online.idle.error'],
    buttonIcon: 'warning-circle-fill',
    labelIcon: 'warning-circle-fill',
    status: 'danger',
    label: 'Something went wrong',
  }
)

type DeviceModeButtonProps = Record<DeviceMode, DNAButtonProps>
const deviceModeButtonProps: DeviceModeButtonProps = {
  desktop: {
    appearance: 'outline',
    status: 'tertiary',
    size: 'lg',
  },
  tablet: {
    appearance: 'ghost',
    status: 'subtle',
    size: 'xl',
    colorMode: 'dark',
    rounded: true,
  },
}

const SyncStatusButton: React.FC = () => {
  const { deviceMode, isOfflineEnabled } = useAppSettings();
  const { displayCRMConnectHeaderButton } = useCRMStatus();
  const syncStatus = useSyncStatus();
  const screenNav = useScreenNav();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { centerIconUI2, menuBorder, cardContainer } = deviceModeStyles[deviceMode];
  const currentDeviceModeButtonProps = deviceModeButtonProps[deviceMode];
  const showSyncStatusButton = isOfflineEnabled || displayCRMConnectHeaderButton;

  const syncStatusDef = getSyncStatusDefinition(syncStatus)
  const handlePress = useCallback(
    () => setIsOpen(p => !p),
    [setIsOpen],
  )
  const onLongPress = useCallback(
    () => isOfflineEnabled && screenNav.goTo.SYNC_STATUS_DEBUG(),
    [isOfflineEnabled, screenNav],
  )

  if (!showSyncStatusButton) return null;
  return (
    <DNAPopover
      lazyMount
      placement="bottom"
      interactive={true}
      visible={isOpen}
      style={menuBorder}
      onBackdropPress={handlePress}
      type="menu"
      delayLongPress={1000}
      onLongPress={onLongPress}
    >
      <DNAPopover.Anchor>
        <Rotate disabled={syncStatusDef?.buttonIcon !== 'arrows-clockwise'}>
          <DNAButton
            {...currentDeviceModeButtonProps}
            rounded={syncStatusDef?.buttonIcon === 'arrows-clockwise'}
            onPress={handlePress}
            delayLongPress={1000}
            onLongPress={onLongPress}
            appearance={'ghost'}
            status={'tertiary'}
            size={deviceMode === 'desktop' ? 'md' : 'xl'}
            iconLeft={'arrows-clockwise'}
            colorMode={'light'}
          />
        </Rotate>
        {/* [TODO-PWA]
              - Needs to show warning icon if there are failed files
              - This button minimized render by not taking the entire sync object
              - Maybe need to break out another variable for errorLength
        */}
        <Iffy is={syncStatusDef?.buttonIcon !== 'arrows-clockwise' && isOfflineEnabled}>
          <Pressable
            delayLongPress={1000}
            onLongPress={onLongPress}
            onPress={handlePress}
            style={centerIconUI2}
          >
            <DNAIcon.Styled
              name={syncStatusDef?.buttonIcon}
              status={syncStatusDef?.status}
              size={deviceMode === 'desktop' ? 'sm' : 'md'}
            />
          </Pressable>
        </Iffy>
      </DNAPopover.Anchor>

      <DNAPopover.Content>
        <DNACard >
          <Iffy is={isOfflineEnabled}>
            <DNABox style={cardContainer}>
              <SyncStatusInfo />
            </DNABox>
          </Iffy>
          <Iffy is={displayCRMConnectHeaderButton}>
            <CRMStatus />
          </Iffy>
        </DNACard>
      </DNAPopover.Content>
    </DNAPopover>
  )
}

export const SyncStatusInfo: React.FC = () => {
  const syncState = useSyncState()
  const syncStatusDef = getSyncStatusDefinition(syncState)
  const allDocumentVersionMap = useAllDocumentVersionMap()

  const contentSyncManifest = Object.values(syncState.context.syncManifest)

  const contentEntriesRemaining = contentSyncManifest
    ? `${contentSyncManifest.length} file(s) remaining`
    : ''

  const totalSizeLeft = contentSyncManifest.reduce<number>(
    (acc, entry) => acc + entry.fileSize,
    0,
  )
  const contentSizeLeft =
    contentSyncManifest.length
      ? bytesToSize(totalSizeLeft)
      : '0 MB'

  const isPaused = syncState.matches('online.paused')
  const isSync = syncState.matches('online.sync')
  const isOffline = syncState.matches('offline.idle')
  const isIdleWithWarning = syncState.matches('online.idle.error')
  const hasRemainingSync = contentSyncManifest.length
  const failedSyncEntries = syncState.context.error.syncEntries
  const hasFailedSyncEntries = Object.keys(syncState.context.error.syncEntries).length
  const failedSyncDocumentVersionIds = Object.values(failedSyncEntries)
    .reduce<string[]>((acc, { documentVersionId }) =>
      acc.includes(documentVersionId) ? acc : [...acc, documentVersionId], []);

  const contactSupportMailTo = useCallback(
    () => {
      Linking.openURL('mailto:support@alucio.io', '_self')
    },
    [],
  )

  const handlePausedSync = useCallback(
    async () => {
      if (isPaused) {
        workerChannel.postMessageExtended({ type: 'RESUME_SYNC' })
      }

      if (isSync) {
        workerChannel.postMessageExtended({ type: 'PAUSE_SYNC' })
      }
    },
    [isPaused, isSync],
  )

  const lastSyncDate = localStorage.getItem('lastSyncDate')
  if (lastSyncDate && !hasRemainingSync && !hasFailedSyncEntries && isOffline) {
    const formatDate =  format(parseISO(lastSyncDate), "MMMM d, yyyy 'at' h:mm a")
    return (
      <DNABox
        fill
        appearance="col"
        style={S.statusInfoContainer}
        spacing="md"
      >
        <DNAText h4 >Content successfully downloaded on:</DNAText>
        <DNAText status="tertiary">{formatDate}</DNAText>
      </DNABox>
    )
  }

  return (
    <DNABox
      fill
      appearance="col"
      style={S.statusInfoContainer}
      spacing="lg"
      childFill={2}
    >
      {/* 0. Icons and Status Label */}
      <DNABox spacing="sm" alignY="center">
        <Rotate disabled={syncStatusDef?.labelIcon !== 'arrows-clockwise'}>
          <DNAIcon.Styled
            name={syncStatusDef?.labelIcon}
            status={syncStatusDef?.status}
            size="xl"
          />
        </Rotate>
        <Iffy is={hasFailedSyncEntries && syncStatusDef?.labelIcon !== 'warning-circle-fill'}>
          <DNAIcon.Styled
            status="warning"
            name="warning-circle-fill"
            size="xl"
          />
        </Iffy>
        <DNAText h4>
          { syncStatusDef?.label }
        </DNAText>
      </DNABox>

      {/* 1. Offline label */}
      {/* Use ternaries instead of Iffy due to DNABox bug */}
      {
        (isOffline && hasRemainingSync)
          ? <DNAText status="tertiary">Please connect to the internet to resume download</DNAText>
          : null
      }

      {/* 2.Failed Sync Entries */}
      {
        hasFailedSyncEntries
          ? <DNABox
              fill
              appearance="col"
              spacing="md"
              childFill={2}
          >
            <DNAText status="tertiary">
              The following file(s) have failed to download:
            </DNAText>
            <DNADivider />
            <ScrollView style={S.failedScrollContainer}>
              <DNABox
                fill
                childFill
                appearance="col"
                spacing="md"
                wrap="start"
              >
                {/* [TODO-PWA] - We should put a cap on how many rows to render */}
                {
                  failedSyncDocumentVersionIds
                    .map((value) => (
                      <DNAText
                        key={allDocumentVersionMap[value].model.id}
                        numberOfLines={3}
                        bold
                      >
                        {
                              allDocumentVersionMap[value]
                                ?.relations
                                ?.documentORM
                                ?.relations
                                .version
                                .latestPublishedDocumentVersionORM
                                ?.model
                                .title
                            }
                      </DNAText>
                    ))
                }
              </DNABox>
            </ScrollView>
            <DNADivider />
          </DNABox>
          : null
        }

      {/* 3. Download Progress */}
      {
        hasRemainingSync
          ? <DNABox fill appearance="col">
            {/* Download remaining */}
            <DNAText b1 status="tertiary">{contentEntriesRemaining}</DNAText>
            <DNAText b1 status="tertiary">{contentSizeLeft} remaining</DNAText>
          </DNABox>
          : null
      }

      {/* 4. Pause Button */}
      {
        (isPaused || isSync)
          ? <DNAButton
              onPress={handlePausedSync}
              stretch
          >
            {isPaused ? 'Resume download' : 'Pause Download'}
          </DNAButton>
          : null
      }

      {/* 5. Contact Support Button */}
      {
        isIdleWithWarning
          ? <DNAButton
              onPress={contactSupportMailTo}
              stretch
          >
            Contact support
          </DNAButton>
          : null
      }
    </DNABox>
  )
}

export default SyncStatusButton
